import { getPromptCacheRetentionTtlMs } from "@arti/ai-models-config/promptCacheRetention";
import { motion, useAnimationControls } from "framer-motion";
import { type FC, useEffect, useState } from "react";

import { colors } from "../../theme/design-system";
import { formatAbbrevNumber } from "../../utils/currency";
import { Sparkline } from "../common/Sparkline";
import { ActorLevelChip } from "./ActorLevelChip";
import {
  CACHE_HIT_DRIFT_DOWN_CLASS,
  CACHE_HIT_DRIFT_UP_CLASS,
  CACHE_HIT_TEXT_CLASS,
  RUN_COUNT_WARNING_TEXT_CLASS,
  type BlackboardInstance,
  type ThermalTone,
  getInstanceDisplayState,
  getInstanceDisplayStyle,
  getInstanceStatusAccentBarClass,
  getThermalToneStyle,
  wakeBlockBadgeLabel,
} from "./blackboardModels";
import { getAvatarBackgroundColor, InstanceAvatar } from "./InstanceAvatar";
import {
  getInstanceActorWakeCacheHitPct,
  getInstanceLifetimeTokenTotal,
  getInstanceRestartWarningCount,
} from "./instanceCardMetrics";
import { McpServerChip } from "./McpServerChip";
import { ModelChip } from "./ModelChip";
import { ReasoningEffortChip } from "./ReasoningEffortChip";
import { useInstanceTokenSeries } from "./useInstanceTokenSeries";

interface InstanceCardProps {
  instance: BlackboardInstance;
  /** Maps a server id to its display name, resolved once by the fleet view. */
  mcpServerNames?: ReadonlyMap<string, string>;
  /** True while the actor is actively generating tokens (live LLM call). */
  isGenerating?: boolean;
  /** "focus" spans 2 cols and shows live sparkline + last tool call. */
  variant?: "focus" | "compact";
  /** One-shot bounce + colored shimmer used to indicate cross-page selection
   * (e.g. clicked an avatar on the Templates lineage row). Color is taken
   * from the actor's avatar background palette so the highlight feels owned
   * by that actor and not generic. Cleared by the parent after ~1.4s. */
  highlighted?: boolean;
  onClick: () => void;
}

export const InstanceCard: FC<InstanceCardProps> = ({
  instance,
  mcpServerNames,
  isGenerating = false,
  variant = "compact",
  highlighted = false,
  onClick,
}) => {
  const displayState = getInstanceDisplayState(instance, isGenerating);
  const displayStyle = getInstanceDisplayStyle(displayState);
  const wakeBlockBadge = wakeBlockBadgeLabel(instance);
  const accent = getAvatarBackgroundColor(instance.avatarSeed);
  const accentR = Number.parseInt(accent.slice(1, 3), 16);
  const accentG = Number.parseInt(accent.slice(3, 5), 16);
  const accentB = Number.parseInt(accent.slice(5, 7), 16);
  const accentBright = `rgba(${accentR}, ${accentG}, ${accentB}, 1)`;
  const accentDim = `rgba(${accentR}, ${accentG}, ${accentB}, 0.15)`;

  // Imperative animation controls drive the highlight blink. A conditionally
  // Rendered motion.div with declarative `animate={[null, dim, bright]}`
  // Silently no-ops on fresh mount because framer-motion's "current value"
  // Still equals the initial value, so the keyframe sequence is treated
  // As already-at-target. controls.start() bypasses that comparison.
  // Explicit `[bright, dim, bright]` keyframes (vs repeatType:"reverse")
  // Ensure the bar actually settles back at full bright between dims.
  const barControls = useAnimationControls();
  useEffect(() => {
    if (!highlighted) {
      return;
    }
    void barControls.start({
      backgroundColor: [accentBright, accentDim, accentBright],
      transition: { duration: 0.25, ease: "easeInOut", repeat: 3 },
    });
  }, [highlighted, barControls, accentBright, accentDim]);
  const totalTokens = getInstanceLifetimeTokenTotal(instance);
  const cacheHitPct = getInstanceActorWakeCacheHitPct(instance);
  const restartWarningCount = getInstanceRestartWarningCount(instance);
  return (
    <motion.button
      className="group relative flex w-full flex-col gap-0 border border-slate-200 bg-white text-left dark:border-slate-800 dark:bg-slate-900"
      onClick={onClick}
      type="button"
      whileHover={{ y: -1 }}
      transition={{ duration: 0.15, ease: "easeOut" }}
    >
      {/* Accent bar — status color (emerald/amber/rose); pulses while
          generating; on cross-page select, briefly swaps to the actor's
          avatar color and blinks a few times in short succession before
          reverting. Same height as normal so the card geometry stays put. */}
      {highlighted ? (
        <motion.div
          key="highlight-bar"
          className="h-0.5 w-full"
          initial={{ backgroundColor: accentBright }}
          animate={barControls}
        />
      ) : (
        <div
          className={`h-0.5 w-full transition-all duration-300 ${getInstanceStatusAccentBarClass(instance.status)} ${isGenerating ? "animate-pulse" : ""}`}
        />
      )}

      <div className="flex flex-col gap-3 p-4">
        {/* Identity row: avatar + name + status. Chips moved to their own row
            below so the name/status can use the full card width and never get
            squeezed by the level/model/effort pills. */}
        <div className="flex min-w-0 items-center gap-3">
          <InstanceAvatar
            seed={instance.avatarSeed}
            status={instance.status}
            animated
          />
          <div className="min-w-0">
            {/* The MCP marker rides the name line rather than the chip row
                below. That row already holds a level, a model and sometimes a
                reasoning-effort chip, and a fourth item wrapped it onto a second
                line -- making these cards taller than their neighbours in a grid
                of cards, which is a worse cost than not spelling the name here.
                The name is in the tooltip and the inspector. */}
            <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-900 dark:text-white">
              <span className="truncate">{instance.displayName}</span>
              <McpServerChip
                compact
                overridden={
                  instance.overrides?.mcpServerIdsSource === "instance"
                }
                serverIds={instance.overrides?.mcpServerIds ?? []}
                serverNames={mcpServerNames}
              />
            </p>
            <div className="mt-0.5 flex items-center gap-1.5">
              {displayStyle.pulseClass !== null && (
                <span
                  className={`h-1.5 w-1.5 rounded-full ${displayStyle.pulseClass}`}
                />
              )}
              <p
                className={`font-mono text-[11px] tracking-tight ${displayStyle.textClass}`}
                title={displayStyle.tooltip ?? undefined}
              >
                {displayState}
              </p>
            </div>
            {wakeBlockBadge && (
              <WakeBlockedChip
                label={wakeBlockBadge.label}
                title={wakeBlockBadge.title}
              />
            )}
          </div>
        </div>

        {/* Focus-only live sparkline row */}
        {variant === "focus" && (
          <FocusLiveRow instanceId={instance.id} isGenerating={isGenerating} />
        )}

        {/* Metadata chip row — level / model / effort, sitting just above the
            divider. Wraps if the card is narrow rather than clipping. */}
        <div className="flex flex-wrap items-center gap-1.5">
          <ActorLevelChip level={instance.evolution} />
          {instance.modelRef && <ModelChip modelRef={instance.modelRef} />}
          {instance.overrides?.reasoningEffort && (
            <ReasoningEffortChip
              compact
              override={instance.overrides.reasoningEffortOverride}
              reasoningEffort={instance.overrides.reasoningEffort}
              source={instance.overrides.reasoningEffortSource}
            />
          )}
        </div>

        {/* Footer stats — channel chips removed: the fleet is grouped by
            channel, so shared membership is already conveyed by the group the
            card sits in, and every actor's private channel just restates its
            name. pt matches the parent flex gap-3 (12px) for symmetric spacing
            around the divider. */}
        <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-3 dark:border-slate-800">
          <div className="flex flex-shrink-0 items-center gap-2 whitespace-nowrap">
            {restartWarningCount !== null && (
              <span
                className={`font-mono text-[10px] tabular-nums ${RUN_COUNT_WARNING_TEXT_CLASS}`}
                title={`${restartWarningCount} restart${restartWarningCount !== 1 ? "s" : ""}`}
              >
                ⚠ {restartWarningCount}r
              </span>
            )}
            <ThermalIndicator instance={instance} isGenerating={isGenerating} />
            {cacheHitPct !== null && (
              <CacheHitChip
                lifetimePct={cacheHitPct}
                lastPct={instance.lastCacheStats?.cacheHitPct ?? null}
              />
            )}
            <span
              className="inline-flex items-baseline gap-1 font-mono text-[10px] tabular-nums text-slate-400 dark:text-slate-500"
              title={`Lifetime tokens · input + output · ${totalTokens.toLocaleString()}`}
            >
              <span>{formatAbbrevNumber(totalTokens, "locale")}</span>
              <span className="text-[8px] font-bold uppercase tracking-wider text-slate-300 dark:text-slate-600">
                lifetime
              </span>
            </span>
          </div>
        </div>
      </div>

      {/* Hover tint overlay */}
      <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-200 group-hover:opacity-100 dark:bg-white/[0.02]" />
    </motion.button>
  );
};

const WakeBlockedChip: FC<{ label: string; title: string }> = ({
  label,
  title,
}) => (
  <span
    className="mt-1 inline-flex max-w-full items-center border border-amber-300 bg-amber-50 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wide text-amber-800 dark:border-amber-400/30 dark:bg-amber-950/40 dark:text-amber-200"
    title={title}
  >
    <span className="truncate">{label}</span>
  </span>
);

// ---------------------------------------------------------------------------
// Cache hit chip — surfaces drift between lifetime and last-activation rate
// ---------------------------------------------------------------------------
//
// The chip shows the lifetime percentage; when the most recent actor wake
// diverges by ≥10 percentage points, an arrow whispers the direction. Same
// data lives in the tooltip; the arrow just makes drift legible without
// forcing a hover.

const DRIFT_THRESHOLD_PP = 10;

const CacheHitChip: FC<{ lifetimePct: number; lastPct: number | null }> = ({
  lifetimePct,
  lastPct,
}) => {
  const lastRounded = lastPct !== null ? Math.round(lastPct) : null;
  const drift = lastRounded !== null ? lastRounded - lifetimePct : 0;
  const arrow = getCacheDriftArrow(drift);
  const arrowClass =
    drift <= -DRIFT_THRESHOLD_PP
      ? CACHE_HIT_DRIFT_DOWN_CLASS
      : CACHE_HIT_DRIFT_UP_CLASS;
  const titleText =
    lastRounded !== null
      ? `Actor wakes ${lifetimePct}% — last activation ${lastRounded}%. Both use cache_read / (cache_read + uncached); background wakes excluded.`
      : `Actor-wake cache hit ${lifetimePct}% (cache_read / (cache_read + uncached); background wakes excluded)`;
  return (
    <span
      className={`flex items-baseline gap-0.5 font-mono text-[10px] tabular-nums ${CACHE_HIT_TEXT_CLASS}`}
      title={titleText}
    >
      {lifetimePct}%
      {arrow !== null && <span className={arrowClass}>{arrow}</span>}
    </span>
  );
};

function getCacheDriftArrow(drift: number): "↑" | "↓" | null {
  if (drift <= -DRIFT_THRESHOLD_PP) {
    return "↓";
  }
  if (drift >= DRIFT_THRESHOLD_PP) {
    return "↑";
  }
  return null;
}

// ---------------------------------------------------------------------------
// Focus-only sub-component — extra subscriptions only mount when focused
// ---------------------------------------------------------------------------

const FocusLiveRow: FC<{ instanceId: string; isGenerating: boolean }> = ({
  instanceId,
  isGenerating,
}) => {
  const tokenSeries = useInstanceTokenSeries(instanceId);
  const recentMax = tokenSeries.length > 0 ? Math.max(...tokenSeries) : 0;

  return (
    <div className="flex items-center justify-between gap-3 rounded-none border border-slate-100 bg-slate-50/50 px-3 py-2 dark:border-slate-800 dark:bg-slate-800/30">
      <div className="min-w-0">
        <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400 dark:text-slate-500">
          [ live ]
        </p>
        <span className="mt-0.5 block font-mono text-[10px] tabular-nums text-slate-400 dark:text-slate-500">
          peak {formatAbbrevNumber(recentMax, "locale")}/s
        </span>
      </div>
      <Sparkline
        values={tokenSeries}
        color={isGenerating ? colors.success : colors.textMuted}
        width={140}
        height={28}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Thermal indicator — forward-looking cache lifetime signal
// ---------------------------------------------------------------------------
//
// HOT  — running; cache is in active use (next step reuses live entry)
// WARM — last *actor wake* is inside the model cache TTL AND prompt unchanged since
// COLD — never had an actor wake, TTL expired, OR prompt edited after last wake
//
// We bind warmth to `step_kind='actor_wake'` (excluding consolidation /
// Cache_warm / compact_seed) because those background passes use a different
// `system_prompt_sha` than the act-phase prefix — they update `last_parked_at`
// But do not refresh the cache the next *actor* wake will hit. Pre-fix, a
// 09:35 consolidation pass would flip the badge to warm even if the actor's
// Last act wake was yesterday afternoon. GPT-5.5 uses the OpenAI extended
// Retention window; other models keep their provider-specific default.
//
// `systemPromptEditedAt > lastActorWakeAt` is the second cold-trigger: a
// Prompt edit busts cache deterministically before the next wake pays for it.

const THERMAL_TICK_MS = 30 * 1000;

const ThermalIndicator: FC<{
  instance: BlackboardInstance;
  isGenerating: boolean;
}> = ({ instance, isGenerating }) => {
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (
      instance.status === "killed" ||
      instance.status === "running" ||
      isGenerating ||
      instance.lastActorWakeAt === null
    ) {
      return;
    }
    const handle = window.setInterval(() => {
      setNowMs(Date.now());
    }, THERMAL_TICK_MS);
    return () => window.clearInterval(handle);
  }, [instance.lastActorWakeAt, instance.status, isGenerating]);

  if (instance.status === "killed" || isGenerating) {
    return null;
  }
  if (instance.status === "running") {
    return (
      <ThermalChip tone="hot" title="Hot — cache in active use this run" />
    );
  }
  const lastWakeMs = instance.lastActorWakeAt
    ? nowMs - Date.parse(instance.lastActorWakeAt)
    : null;
  const promptEditedAfterWake =
    instance.lastActorWakeAt !== null &&
    instance.systemPromptEditedAt !== null &&
    Date.parse(instance.systemPromptEditedAt) >
      Date.parse(instance.lastActorWakeAt);
  const wasBreak = instance.lastCacheStats?.isBreak === true;
  const cacheTtlMs = getPromptCacheRetentionTtlMs(
    instance.modelRef,
    instance.overrides?.systemCacheTtl ?? undefined,
  );
  const ttlExpired = lastWakeMs !== null && lastWakeMs >= cacheTtlMs;
  const isCold = lastWakeMs === null || ttlExpired || promptEditedAfterWake;

  if (isCold) {
    const reason = getColdCacheReason({
      cacheTtlMs,
      lastWakeMs,
      promptEditedAfterWake,
    });
    const breakNote = wasBreak ? "; last wake was a cache break" : "";
    return (
      <ThermalChip
        tone="cold"
        title={`Cold — ${reason}${breakNote}. Next wake pays a fresh 2x cache write.`}
      />
    );
  }
  return (
    <ThermalChip
      tone="warm"
      title={`Warm — last actor wake ${formatDuration(lastWakeMs ?? 0)} ago, ${formatDuration(cacheTtlMs)} TTL cache still alive${wasBreak ? "; last wake was a cache break" : ""}.`}
    />
  );
};

const ThermalChip: FC<{ tone: ThermalTone; title: string }> = ({
  tone,
  title,
}) => {
  const style = getThermalToneStyle(tone);
  return (
    <span
      className={`font-mono text-[10px] tabular-nums ${style.textClass}`}
      title={title}
    >
      {style.glyph} {tone}
    </span>
  );
};

function getColdCacheReason(input: {
  cacheTtlMs: number;
  lastWakeMs: number | null;
  promptEditedAfterWake: boolean;
}): string {
  if (input.lastWakeMs === null) {
    return "no actor wake yet";
  }
  if (input.promptEditedAfterWake) {
    return "prompt edited after last wake";
  }
  return `last actor wake ${formatDuration(input.lastWakeMs)} ago — past ${formatDuration(input.cacheTtlMs)} TTL`;
}

function formatDuration(ms: number): string {
  const min = Math.floor(ms / 60_000);
  if (min < 60) {
    return `${min}m`;
  }
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m > 0 ? `${h}h${m}m` : `${h}h`;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
